import { Landmark, ShieldCheck, PiggyBank, FileCheck2, PhoneCall, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { L, Sel, Txt, YesNo, ReadOut, Section, inr, TONES, Badge, computeLap, countFilled, valuationVals, detailsVals, docsVals, emiVals,
  ValuationSection, DetailsSection, DocsSection as PropertyDocsSection, EmiSection } from "@/components/app/LapSections";

const EXISTING_TYPES = ["Home Loan", "LAP (Loan Against Property)", "Business Loan", "Personal Loan"];
const SECURED = ["Home Loan", "LAP (Loan Against Property)"];
const DOCS = [["sanction", "Sanction Letter Received?"], ["statement", "Last 12 Months Bank Statement Received?"],
  ["foreclosure", "Foreclosure Letter Received? (loan close panna evalo letter)"], ["salary", "Salary Slip / IT Return Received?"],
  ["property", "Property Documents Copy Received?"]];

export const isTakeOver = (lp) => (lp || "").trim().toUpperCase() === "TAKE OVER";
const num = (v) => parseFloat(v) || 0;
const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";
const isSecured = (t) => SECURED.includes(t.existing_type);
const months = (y, m) => num(y) * 12 + num(m);

function emiOf(P, annual, n) {
  if (P <= 0 || n <= 0) return 0;
  const r = annual / 12 / 100;
  if (r === 0) return P / n;
  const f = Math.pow(1 + r, n);
  return (P * r * f) / (f - 1);
}

function payingYears(t) {
  if (t.start_date) {
    const d = new Date(t.start_date);
    if (!isNaN(d)) return Math.max(0, Math.round(((Date.now() - d.getTime()) / (365.25 * 864e5)) * 10) / 10);
  }
  return has(t.years_paying) ? num(t.years_paying) : null;
}

function balanceStatus(t) {
  const est = num(t.est_outstanding), ver = num(t.verified_outstanding);
  if (!est || !ver) return null;
  const pct = (Math.abs(est - ver) / ver) * 100;
  if (pct <= 10) return { tone: "green", text: "OK - Customer aware", pct };
  if (pct <= 25) return { tone: "yellow", text: "Mismatch - Explain to customer", pct };
  return { tone: "red", text: "High Gap - Customer may be shocked, handle carefully", pct };
}

function bounceStatus(t) {
  if (!has(t.bounces_12m)) return null;
  const b = num(t.bounces_12m);
  if (b <= 0) return { tone: "green", text: "Clean - Take over eligible" };
  if (b <= 2) return { tone: "yellow", text: "Risky - Bank may ask explanation" };
  return { tone: "red", text: "High Risk - Take over likely to be rejected" };
}

function cibilStatus(t) {
  if (!has(t.cibil)) return null;
  const c = num(t.cibil);
  if (c >= 750) return { tone: "green", text: "Excellent" };
  if (c >= 700) return { tone: "green", text: "Good" };
  if (c >= 650) return { tone: "yellow", text: "Average - limited bank options" };
  return { tone: "red", text: "Poor - take over difficult" };
}

export function computeTakeover(t = {}) {
  const remN = months(t.rem_years, t.rem_months);
  const newN = has(t.new_years) || has(t.new_months) ? months(t.new_years, t.new_months) : remN;
  const baseBal = has(t.verified_outstanding) ? num(t.verified_outstanding) : num(t.est_outstanding);
  const newLoan = baseBal + (t.topup === "yes" ? num(t.topup_amount) : 0);
  const newEmi = emiOf(newLoan, num(t.new_rate), newN);
  const totalSavings = num(t.current_emi) * remN - newEmi * newN;
  const docKeys = DOCS.map(d => d[0]).filter(k => k !== "property" || isSecured(t));
  return {
    paying_years: payingYears(t), balance: balanceStatus(t), bounce: bounceStatus(t), cibil: cibilStatus(t),
    rem_months_total: remN, new_months_total: newN, using_estimate: !has(t.verified_outstanding) && has(t.est_outstanding),
    new_loan_amount: newLoan, new_emi: Math.round(newEmi), monthly_savings: Math.round(num(t.current_emi) - newEmi),
    total_savings: Math.round(totalSavings), net_benefit: Math.round(totalSavings - num(t.foreclosure_charges) - num(t.processing_fee)),
    docs_received: docKeys.filter(k => t[`doc_${k}`] === "yes").length, docs_total: docKeys.length,
    property: computeLap(t), total_other_emi: computeLap(t).total_emi,
  };
}

export function takeoverProgress(t = {}) {
  const vals = ["bank_name", "branch", "existing_type", "original_amount", "current_rate", "current_emi", "est_outstanding",
    "verified_outstanding", "total_bounces", "bounces_12m", "missed_paid_later", "cibil", "new_rate", "topup",
    "foreclosure_charges", "co_app_name", "co_app_phone", "rm_name", "rm_phone", "docs_wa", "call_time", "urgency",
    "applied_elsewhere", "next_followup"].map(k => t[k]);
  vals.push(t.start_date || t.years_paying, t.rem_years || t.rem_months);
  if (t.missed_paid_later === "yes") vals.push(t.missed_count);
  if (t.topup === "yes") vals.push(t.topup_amount);
  if (t.applied_elsewhere === "yes") vals.push(t.applied_where);
  vals.push(...valuationVals(t), ...detailsVals(t), ...docsVals(t, true), t.market_value, t.docs_with);
  vals.push(...emiVals(t));
  DOCS.forEach(([k]) => { if (k !== "property" || isSecured(t)) vals.push(t[`doc_${k}`]); });
  return countFilled(vals);
}

function Hint({ children }) {
  return <div className="text-[11px] text-amber-300/80 mt-1">{children}</div>;
}

function Tenure({ tid, label, y, m, onY, onM, placeholderY, placeholderM }) {
  return (
    <div>
      <L>{label}</L>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <Input data-testid={`to-${tid}-years`} type="number" value={y ?? ""} placeholder={placeholderY ?? "Years"} onChange={e => onY(e.target.value)} className="bg-slate-950 border-slate-700 h-11"/>
        <Input data-testid={`to-${tid}-months`} type="number" value={m ?? ""} placeholder={placeholderM ?? "Months"} onChange={e => onM(e.target.value)} className="bg-slate-950 border-slate-700 h-11"/>
      </div>
    </div>
  );
}

function ExistingSection({ t, set, c }) {
  return (
    <Section id="to-existing" title="Existing Loan Details" icon={Landmark}>
      <Txt tid="to-bank-name" label="Existing Bank / NBFC Name" req value={t.bank_name} onChange={v => set({ bank_name: v })}/>
      <Txt tid="to-branch" label="Bank Branch" req value={t.branch} placeholder="e.g. Tenkasi branch" onChange={v => set({ branch: v })}/>
      <Sel tid="to-existing-type" label="Existing Loan Type" req value={t.existing_type} onChange={v => set({ existing_type: v })} options={EXISTING_TYPES}/>
      <div>
        <div className="grid grid-cols-2 gap-2">
          <Txt tid="to-start-date" label="Loan Start Date" type="date" value={t.start_date} onChange={v => set({ start_date: v })}/>
          <Txt tid="to-years-paying" label="OR Years Paying" type="number" value={t.years_paying} onChange={v => set({ years_paying: v })}/>
        </div>
        {c.paying_years !== null && <div className="text-[11px] text-emerald-300 mt-1" data-testid="to-paying-since">Paying since {c.paying_years} years</div>}
      </div>
      <Txt tid="to-original-amount" label="Original Loan Amount (₹)" type="number" value={t.original_amount} onChange={v => set({ original_amount: v })}/>
      <Txt tid="to-current-rate" label="Current Interest Rate %" req type="number" value={t.current_rate} placeholder="e.g. 12.5" onChange={v => set({ current_rate: v })}/>
      <Txt tid="to-current-emi" label="Current Monthly EMI (₹)" req type="number" value={t.current_emi} onChange={v => set({ current_emi: v })}/>
      <Tenure tid="rem" label="Tenure Remaining" y={t.rem_years} m={t.rem_months} onY={v => set({ rem_years: v })} onM={v => set({ rem_months: v })}/>
      <div>
        <Txt tid="to-est-outstanding" label="Customer Estimated Outstanding (₹)" type="number" value={t.est_outstanding} onChange={v => set({ est_outstanding: v })}/>
        <Hint>Customer's own estimate - verify with bank statement</Hint>
      </div>
      <div>
        <Txt tid="to-verified-outstanding" label="Verified Outstanding Balance (₹)" type="number" value={t.verified_outstanding} onChange={v => set({ verified_outstanding: v })}/>
        <Hint>Fill after receiving bank statement</Hint>
      </div>
      <div className="sm:col-span-2">
        <L>Balance Match Status</L>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <Badge s={c.balance} tid="to-balance-status"/>
          {c.balance && <span className="text-[11px] text-slate-400">{c.balance.pct.toFixed(1)}% difference</span>}
        </div>
      </div>
    </Section>
  );
}

function TrackSection({ t, set, c }) {
  return (
    <Section id="to-track" title="Repayment Track Record (Bank approval ku critical!)" icon={ShieldCheck}>
      <div>
        <Txt tid="to-total-bounces" label="Total Bounces So Far" req type="number" value={t.total_bounces} onChange={v => set({ total_bounces: v })}/>
        <Hint>Ask politely - customer may underreport</Hint>
      </div>
      <div>
        <Txt tid="to-bounces-12m" label="Bounces in Last 12 Months" req type="number" value={t.bounces_12m} onChange={v => set({ bounces_12m: v })}/>
        <div className="mt-2"><Badge s={c.bounce} tid="to-bounce-status"/></div>
      </div>
      {c.bounce?.tone === "red" && (
        <div data-testid="to-bounce-warning" className="sm:col-span-2 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-300 font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0"/> 3+ bounces in 12 months - most banks will reject take over. Get senior review.
        </div>
      )}
      <YesNo tid="to-missed-paid-later" label="Any EMI Missed But Paid Later?" value={t.missed_paid_later} onChange={v => set({ missed_paid_later: v })}/>
      {t.missed_paid_later === "yes" && <Txt tid="to-missed-count" label="How many times" type="number" value={t.missed_count} onChange={v => set({ missed_count: v })}/>}
      <div>
        <Txt tid="to-cibil" label="CIBIL Score (optional)" type="number" value={t.cibil} onChange={v => set({ cibil: v })}/>
        <div className="mt-2"><Badge s={c.cibil} tid="to-cibil-status"/></div>
      </div>
    </Section>
  );
}

function ToneBox({ tid, label, value, tone, note }) {
  return (
    <div className={`rounded-md border p-3 ${TONES[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-mono-alt text-lg mt-1" data-testid={tid}>{value}</div>
      {note && <div className="text-[11px] mt-1 font-semibold" data-testid={`${tid}-note`}>{note}</div>}
    </div>
  );
}

function SavingsSection({ t, set, c }) {
  const ms = c.monthly_savings;
  const nb = c.net_benefit;
  return (
    <Section id="to-savings" title="Take Over Savings Calculator" icon={PiggyBank}>
      <Txt tid="to-new-rate" label="Expected New Interest Rate %" req type="number" value={t.new_rate} placeholder="e.g. 9.0" onChange={v => set({ new_rate: v })}/>
      <Tenure tid="new" label="New Tenure (default = remaining)" y={t.new_years} m={t.new_months} onY={v => set({ new_years: v })} onM={v => set({ new_months: v })}
        placeholderY={t.rem_years ? `${t.rem_years} yrs` : "Years"} placeholderM={t.rem_months ? `${t.rem_months} mo` : "Months"}/>
      <YesNo tid="to-topup" label="Top-up Required?" value={t.topup} onChange={v => set({ topup: v })}/>
      {t.topup === "yes" && <Txt tid="to-topup-amount" label="Top-up Amount (₹)" type="number" value={t.topup_amount} onChange={v => set({ topup_amount: v })}/>}
      <ReadOut tid="to-new-loan-amount" label="New Loan Amount" value={inr(c.new_loan_amount)} hint={c.using_estimate ? "Using customer estimate until verified balance is filled" : null}/>
      <ReadOut tid="to-new-emi" label="New Monthly EMI" value={inr(c.new_emi)} hint={`${c.new_months_total} months`}/>
      <ToneBox tid="to-monthly-savings" label="Monthly Savings" value={inr(ms)} tone={ms > 0 ? "green" : "yellow"}
        note={ms > 0 ? `Customer saves ${inr(ms)} every month!` : "No monthly savings - check tenure or rate"}/>
      <Txt tid="to-foreclosure" label="Foreclosure / Prepayment Charges (₹)" type="number" value={t.foreclosure_charges} onChange={v => set({ foreclosure_charges: v })}/>
      <ReadOut tid="to-total-savings" big label="Total Interest Savings" value={inr(c.total_savings)} hint={`Total savings over loan period: ${inr(c.total_savings)}`}/>
      <Txt tid="to-processing-fee" label="Processing Fee (₹, optional)" type="number" value={t.processing_fee} onChange={v => set({ processing_fee: v })}/>
      <ToneBox tid="to-net-benefit" label="Net Benefit" value={inr(nb)} tone={nb > 0 ? "green" : "red"} note={nb > 0 ? "Worth doing!" : "Not beneficial - do not proceed"}/>
    </Section>
  );
}

function PropertyExtra({ t, set }) {
  return (
    <>
      <Txt tid="to-market-value" label="Current Market Value (₹)" type="number" value={t.market_value} onChange={v => set({ market_value: v })}/>
      <div>
        <Sel tid="to-docs-with" label="Property Documents With Whom?" value={t.docs_with} onChange={v => set({ docs_with: v })} options={["Existing Bank", "Customer", "Other"]}/>
        <Hint>Original documents are usually with the existing bank</Hint>
      </div>
    </>
  );
}

function DocsSection({ t, set, c }) {
  return (
    <Section id="to-docs" title="Documents Checklist" icon={FileCheck2}>
      <div className="sm:col-span-2 text-xs font-bold text-emerald-300" data-testid="to-docs-progress">{c.docs_received}/{c.docs_total} documents received</div>
      {DOCS.filter(([k]) => k !== "property" || isSecured(t)).map(([k, label]) => (
        <div key={k} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <YesNo tid={`to-doc-${k}`} label={label} value={t[`doc_${k}`]} onChange={v => set({ [`doc_${k}`]: v })}/>
          {t[`doc_${k}`] === "yes" && (
            <Input data-testid={`to-doc-${k}-date`} type="date" value={t[`doc_${k}_date`] || ""} onChange={e => set({ [`doc_${k}_date`]: e.target.value })}
              className="bg-slate-950 border-slate-700 h-11 w-full sm:w-40"/>
          )}
        </div>
      ))}
    </Section>
  );
}

function ContactSection({ t, set }) {
  const bad = (p) => p && !/^\d{10}$/.test(p);
  const ph = (v) => v.replace(/\D/g, "").slice(0, 10);
  return (
    <Section id="to-contact" title="Contact & Follow-up" icon={PhoneCall}>
      <Txt tid="to-co-app-name" label="Co-applicant Name" value={t.co_app_name} onChange={v => set({ co_app_name: v })}/>
      <div>
        <Txt tid="to-co-app-phone" label="Co-applicant Phone" value={t.co_app_phone} placeholder="10 digits" extra={{ inputMode: "numeric", maxLength: 10 }} onChange={v => set({ co_app_phone: ph(v) })}/>
        {bad(t.co_app_phone) && <div className="text-[11px] text-red-400 mt-1" data-testid="to-co-app-phone-error">Enter a valid 10-digit phone number</div>}
      </div>
      <Txt tid="to-rm-name" label="Existing Bank RM Name" value={t.rm_name} onChange={v => set({ rm_name: v })}/>
      <div>
        <Txt tid="to-rm-phone" label="Existing Bank RM Phone" value={t.rm_phone} placeholder="10 digits" extra={{ inputMode: "numeric", maxLength: 10 }} onChange={v => set({ rm_phone: ph(v) })}/>
        {bad(t.rm_phone) && <div className="text-[11px] text-red-400 mt-1" data-testid="to-rm-phone-error">Enter a valid 10-digit phone number</div>}
      </div>
      <YesNo tid="to-docs-wa" label="Documents Received on WhatsApp?" value={t.docs_wa} onChange={v => set({ docs_wa: v })}/>
      <Sel tid="to-call-time" label="Convenient Call Time" value={t.call_time} onChange={v => set({ call_time: v })} options={["Morning", "Afternoon", "Evening"]}/>
      <Sel tid="to-urgency" label="Loan Urgency" value={t.urgency} onChange={v => set({ urgency: v })} options={["Within 15 days", "Within 1 month", "Within 3 months", "Just Enquiry"]}/>
      <YesNo tid="to-applied-elsewhere" label="Applied Elsewhere for Take Over?" value={t.applied_elsewhere} onChange={v => set({ applied_elsewhere: v })}/>
      {t.applied_elsewhere === "yes" && <Txt tid="to-applied-where" label="Where" value={t.applied_where} onChange={v => set({ applied_where: v })}/>}
      <Txt tid="to-next-followup" label="Next Follow-up Date" type="date" value={t.next_followup} onChange={v => set({ next_followup: v })}/>
    </Section>
  );
}

export default function TakeOverSections({ value, onChange, districts = [] }) {
  const t = value || {};
  const set = (patch) => onChange({ ...t, ...patch });
  const c = computeTakeover(t);
  const { filled, total } = takeoverProgress(t);
  const pct = total ? Math.round((filled / total) * 100) : 0;
  return (
    <div className="space-y-3" data-testid="takeover-sections">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <div className="flex items-center justify-between text-xs">
          <span className="uppercase tracking-widest text-amber-400 font-bold text-[10px]">Take Over Profile</span>
          <span className="font-mono-alt text-amber-300" data-testid="to-progress-text">{filled}/{total} fields completed</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-400 transition-[width] duration-500" style={{ width: `${pct}%` }}/>
        </div>
      </div>
      <ExistingSection t={t} set={set} c={c}/>
      <TrackSection t={t} set={set} c={c}/>
      <ValuationSection l={t} set={set}/>
      <DetailsSection l={t} set={set} districts={districts} extra={<PropertyExtra t={t} set={set}/>}/>
      <PropertyDocsSection l={t} set={set} hideExistingLoan/>
      <SavingsSection t={t} set={set} c={c}/>
      <EmiSection l={t} set={set}/>
      <DocsSection t={t} set={set} c={c}/>
      <ContactSection t={t} set={set}/>
    </div>
  );
}
